<template>
  <div>
    <loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true"></loading>
    <div class="ma-0 dashboard-outer-container" id="router">
      <div class="dashboard-inner-wrap">
      <!-- Job Assignment Section -->
      <v-card v-if="canView('JobAssignment')" class="dashboard-card mb-4" elevation="2">
        <v-card-title class="dashboard-header py-2">
          {{ $t('__dashboardJobAssignment') }}
        </v-card-title>
        <v-card-text class="pa-6">
          <v-row>
            <!-- Left: incomplete count + pie chart -->
            <!-- Left: incomplete count + pie chart -->
            <v-col cols="12" md="6" class="px-4 px-md-12">
              <div class="mb-1 stat-item">
                {{ $t('__dashboardIncompleteTasks') }}: <span class="stat-value">{{ jobTotalCount }}</span>
              </div>
              <div v-if="pieReady" class="chart-container" style="height: 220px; display: flex; align-items: center; justify-content: center;">
                <canvas ref="jobPieChart"></canvas>
              </div>
              <div v-else-if="!isLoading" class="text-center grey--text py-8">
                <v-icon size="40" color="#ccc">mdi-chart-pie</v-icon>
                <div class="mt-2">{{ $t('__dashboardNoData') }}</div>
              </div>
              <div class="d-flex justify-end mt-1">
                <v-btn text color="#666" class="view-more-link" @click="redirectPath('/jobAssignmentList')">{{ $t('__dashboardViewMore') }}</v-btn>
              </div>
            </v-col>

            <!-- Right: engineer bar chart (Managers Only - Hidden but occupies space for others) -->
            <v-col cols="12" md="6" class="px-4 px-md-12" :style="{ visibility: isManager ? 'visible' : 'hidden' }">
              <div class="mb-1 stat-item">
                {{ $t('__dashboardEngineerAvgLoad') }}: <span class="stat-value">{{ avgCaseLoad }}</span>
              </div>
              <div class="chart-container" style="height: 220px;">
                <canvas ref="jobBarChart"></canvas>
              </div>
              <div class="d-flex justify-end mt-1">
                <v-btn text color="#666" class="view-more-link" @click="redirectPath('/jobAssignmentList')">{{ $t('__dashboardViewMore') }}</v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Maintenance and Device Expiry Section -->
      <v-row v-if="canView('WarrantyAndMaintenance')" class="mt-2">
        <v-col cols="12" md="6">
          <v-card class="dashboard-card" elevation="2" style="height: 100%;">
            <v-card-title class="dashboard-header py-2">
              {{ $t('__dashboardMaintenanceExpiry') }}
            </v-card-title>
            <v-card-text class="pa-6">
              <div class="mb-2 stat-item clickable-stat" @click="redirectPath('/warrantyAndMaintenance?tab=0&filter=Upcoming')">
                {{ $t('__dashboardMaintenanceExpiringDays', { days: maintenanceReminderDays }) }}: <span class="stat-value">{{ maintenanceStats.expiringX }}</span>
              </div>
              <div class="mb-2 stat-item clickable-stat" @click="redirectPath('/warrantyAndMaintenance?tab=0&filter=Expired')">
                {{ $t('__dashboardMaintenanceExpired') }}: <span class="stat-value">{{ maintenanceStats.expired }}</span>
              </div>
              <div class="d-flex justify-end mt-1">
                <v-btn text color="#666" class="view-more-link" @click="redirectPath('/warrantyAndMaintenance?tab=0')">{{ $t('__dashboardViewMore') }}</v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="dashboard-card" elevation="2" style="height: 100%;">
            <v-card-title class="dashboard-header py-2">
              {{ $t('__dashboardDeviceWarrantyExpiry') }}
            </v-card-title>
            <v-card-text class="pa-6">
              <div class="mb-2 stat-item clickable-stat" @click="redirectPath('/warrantyAndMaintenance?tab=1&filter=Upcoming')">
                {{ $t('__dashboardDeviceExpiringDays', { days: deviceReminderDays }) }}: <span class="stat-value">{{ deviceStats.expiringY }}</span>
              </div>
              <div class="mb-2 stat-item clickable-stat" @click="redirectPath('/warrantyAndMaintenance?tab=1&filter=Expired')">
                {{ $t('__dashboardDeviceExpired') }}: <span class="stat-value">{{ deviceStats.expired }}</span>
              </div>
              <div class="d-flex justify-end mt-1">
                <v-btn text color="#666" class="view-more-link" @click="redirectPath('/warrantyAndMaintenance?tab=1')">{{ $t('__dashboardViewMore') }}</v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  PieController,
  BarController
} from 'chart.js'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.min.css'
import { getEmployeeInfo, getRoles } from '@/utils/auth'

Chart.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  PieController,
  BarController
)

export default {
  name: 'Dashboard',
  components: { Loading },
  data: () => ({
    isLoading: false,
    viewStyle: {},
    isManager: false,  // 改為 data，由 loadUserPermissions() 更新（computed 無法感知 localStorage 變化）
    // Job stats
    jobTotalCount: 0,
    avgCaseLoad: 0,
    // Status map from API: { CodeText -> count }
    statusCountMap: {},
    // Status definitions from TrackStatus API: [{ CodeText, CodeValue(color) }]
    trackStatusList: [],
    // Engineer data from job list
    engineerData: {},
    pieReady: false,
    pieChart: null,
    barChart: null,
    // Maintenance / Device
    maintenanceReminderDays: 30, // Default X
    deviceReminderDays: 30,      // Default Y
    maintenanceStats: { expiringX: 0, expired: 0 },
    deviceStats: { expiringY: 0, expired: 0 },
    userPermissions: [],
  }),
  computed: {
    // isManager 已移至 data，由 loadUserPermissions() 控制（避免 localStorage 非響應式問題）
  },
  watch: {
    // 當切換語系時，重新載入資料並重繪圖表
    '$i18n.locale'() {
      console.log('🌐 [Dashboard] 語系變換，刷新圖表...')
      this.getDashboardData()
    }
  },
  mounted() {
    this.calculateWindows()
    this.getDashboardData()
  },
  created() {
    this.loadUserPermissions()
    this.$root.$on('windowInnerHeight', () => {
      this.calculateWindows()
    })
    // 當前登入者的角色/權限被更新時，重新載入 Dashboard 資料
    this.$root.$on('userPermissionsUpdated', () => {
      console.log('🔄 [Dashboard] 權限已更新，重新載入資料...')
      this.loadUserPermissions()
      this.getDashboardData()
    })
  },
  beforeDestroy() {
    if (this.pieChart) this.pieChart.destroy()
    if (this.barChart) this.barChart.destroy()
  },
  methods: {
    calculateWindows() {
      this.viewStyle = { height: window.innerHeight - 63 + 'px' }
    },
    redirectPath(path) {
      this.$router.push(path).catch(() => {})
    },

    // 解析到期日期，統一轉為本地時間（Local Date），避免 UTC 時差造成計算偏差
    parseDate(val1, val2, val3) {
      const target = val1 || val2 || val3
      if (!target || typeof target !== 'string') return null
      
      const dStr = target.substr(0, 10).replace(/\//g, '-')
      const parts = dStr.split('-')
      if (parts.length === 3) {
        // 使用 new Date(year, monthIndex, day) 會建立本地時區的日期
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
      }
      return null
    },

    async getDashboardData() {
      this.isLoading = true
      try {
        const canViewJob = this.canView('JobAssignment')
        const canViewWarranty = this.canView('WarrantyAndMaintenance')

        // 1. Fetch TrackStatus list (for labels & colors)
        if (canViewJob) await this.fetchTrackStatusList()
        
        // 2. Fetch Expiry Reminder settings (X and Y days)
        if (canViewWarranty) await this.fetchExpiryReminderSettings()
        
        // 3. Fetch Job Assignment list (all pages to get full count + per-status counts)
        if (canViewJob) await this.fetchJobData()
        
        // 4. Fetch Maintenance & Device data
        if (canViewWarranty) {
          await this.fetchMaintenanceData()
          await this.fetchDeviceData()
        }

        // 5. Build charts after data ready
        await this.$nextTick()
        if (canViewJob) {
          this.buildPieChart()
          this.buildBarChart()
        }
      } finally {
        this.isLoading = false
      }
    },
    loadUserPermissions() {
      const employeeInfo = getEmployeeInfo()
      if (employeeInfo && employeeInfo.function) {
        try {
          this.userPermissions = JSON.parse(employeeInfo.function)
        } catch (e) {
          console.error('❌ Failed to parse user permissions:', e)
          this.userPermissions = []
        }
      } else {
        this.userPermissions = []
      }
      // 同步更新 isManager（讀最新的 localStorage role 欄位）
      this.isManager = getRoles().isManager
      console.log('✅ [Dashboard] 角色載入完成, isManager:', this.isManager)
    },
    canView(key) {
      const permission = this.userPermissions.find(p => p.key === key)
      return !!(permission && (permission.view || permission.edit))
    },

    fetchTrackStatusList() {
      return this.$http({
        method: 'GET',
        url: '/CodeTypeList',
        params: { CodeType: 'TrackStatus', currentPage: 1, pageSize: 9999 }
      }).then(res => {
        const rawList = res.data?.Data || res.data?.data || []
        // 排除已結束 (Closed) 的狀態，僅顯示進行中的狀態
        this.trackStatusList = rawList.filter(item => item.CodeText !== 'Closed')
      }).catch(() => {})
    },
    
    fetchExpiryReminderSettings() {
      return this.$http({
        method: 'GET',
        url: '/CodeTypeList',
        params: { CodeType: 'ExpiryReminder', currentPage: 1, pageSize: 9999 }
      }).then(res => {
        const data = res.data?.Data || res.data?.data || []
        if (Array.isArray(data)) {
          const maintenanceSetting = data.find(item => item.CodeText === 'Maintenance Contract')
          if (maintenanceSetting) {
            this.maintenanceReminderDays = parseInt(maintenanceSetting.CodeValue) || 30
          }
          const deviceSetting = data.find(item => item.CodeText === 'Device')
          if (deviceSetting) {
            this.deviceReminderDays = parseInt(deviceSetting.CodeValue) || 30
          }
        }
      }).catch(() => {})
    },

    fetchJobData() {
      return this.$http({
        method: 'GET',
        url: '/GetJobAssignmentList',
        params: { pageSize: 9999, currentPage: 1, orderBy: 'id', direction: 'desc' }
      }).then(res => {
        const rawData = res.data?.Data || res.data?.data || []
        
        // 僅統計非 'Closed' 的工作項目 (Incomplete Tasks)
        let data = Array.isArray(rawData) ? rawData.filter(job => job.status !== 'Closed') : []
        // 2. 根據角色進行二次過濾
        const roles = getRoles()
        if (!roles.isManager) {
          // 工程師：只看指派給自己的任務 (依據 姓名 比對)
          const employeeInfo = getEmployeeInfo()
          const myName = employeeInfo?.name || ''
          data = data.filter(job => job.assigned === myName)
        }

        const totalCount = data.length
        this.jobTotalCount = totalCount

        // Count per status
        const countMap = {}
        const engMap = {}
        data.forEach(job => {
          const st = job.status || 'Unknown'
          countMap[st] = (countMap[st] || 0) + 1
          // Engineer case load
          if (job.assigned) {
            engMap[job.assigned] = (engMap[job.assigned] || 0) + 1
          }
        })
        this.statusCountMap = countMap
        this.engineerData = engMap

        // Average case load
        const engValues = Object.values(engMap)
        this.avgCaseLoad = engValues.length > 0
          ? (engValues.reduce((a, b) => a + b, 0) / engValues.length).toFixed(1)
          : 0
      }).catch(() => {})
    },

    fetchMaintenanceData() {
      return this.$http({
        method: 'GET',
        url: '/GetMaintenanceExpiryStats'
      }).then(res => {
        const data = res.data?.Data || {}
        this.maintenanceStats = { 
          expiringX: data.expiringCount || 0, 
          expired: data.expiredCount || 0 
        }
        this.maintenanceReminderDays = data.reminderDays || 30
      }).catch(() => {})
    },

    fetchDeviceData() {
      return this.$http({
        method: 'GET',
        url: '/GetDeviceExpiryStats'
      }).then(res => {
        const data = res.data?.Data || {}
        this.deviceStats = { 
          expiringY: data.expiringCount || 0, 
          expired: data.expiredCount || 0 
        }
        this.deviceReminderDays = data.reminderDays || 30
      }).catch(() => {})
    },

    buildPieChart() {
      if (this.pieChart) {
        this.pieChart.destroy()
        this.pieChart = null
      }

      // Build labels & data from TrackStatus list
      // Only show statuses that exist in the job data, or all statuses with count 0
      const labels = []
      const counts = []
      const colors = []

      // Fallback palette if no color defined
      const fallbackColors = [
        '#FFD700','#9370DB','#87CEEB','#FFA500','#90EE90','#FF69B4',
        '#C0C0C0','#40E0D0','#FF6347','#7B68EE','#98FB98','#DEB887'
      ]
      let fallbackIdx = 0

      if (this.trackStatusList.length > 0) {
        this.trackStatusList.forEach(s => {
          const label = s.CodeText || ''
          const count = this.statusCountMap[label] || 0
          const color = s.CodeValue && s.CodeValue.startsWith('#')
            ? s.CodeValue
            : fallbackColors[fallbackIdx++ % fallbackColors.length]
          labels.push(label)
          counts.push(count)
          colors.push(color)
        })
      } else {
        // Fallback: build from actual job statuses
        Object.entries(this.statusCountMap).forEach(([label, count], i) => {
          labels.push(label)
          counts.push(count)
          colors.push(fallbackColors[i % fallbackColors.length])
        })
      }

      // Don't draw if everything is 0
      const total = counts.reduce((a, b) => a + b, 0)
      if (total === 0 || labels.length === 0) {
        this.pieReady = false
        return
      }

      this.pieReady = true;
      
      this.$nextTick(() => {
        const ctx = this.$refs.jobPieChart;
        if (!ctx) return;

        // --- Custom Plugin for Data Labels inside Slices ---
        const dataLabelsPlugin = {
          id: 'dataLabelsPlugin',
          afterDraw(chart) {
            const { ctx, data } = chart;
            ctx.save();
            const dataset = data.datasets[0];
            const total = dataset.data.reduce((a, b) => a + b, 0);

            chart.getDatasetMeta(0).data.forEach((arc, i) => {
              const val = dataset.data[i];
              if (val === 0) return;
              const pct = total > 0 ? Math.round((val / total) * 100) : 0;
              const text = `${pct}%(${val})`;
              
              // Find center of arc
              const center = arc.getCenterPoint();
              
              ctx.fillStyle = 'black'; // Or white if pie is dark
              ctx.font = 'bold 11px sans-serif';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(text, center.x, center.y);
            });
            ctx.restore();
          }
        };

        this.pieChart = new Chart(ctx, {
          type: 'pie',
          data: {
            labels,
            datasets: [{
              data: counts,
              backgroundColor: colors,
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          plugins: [dataLabelsPlugin],
          options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 4, bottom: 4, left: 4, right: 4 } },
            plugins: {
              legend: {
                position: 'right',
                labels: {
                  font: { size: 12, family: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif" },
                  padding: 10,
                  usePointStyle: true,
                  pointStyle: 'circle',
                  generateLabels(chart) {
                    const d = chart.data;
                    if (!d.labels.length || !d.datasets.length) return [];
                    return d.labels.map((label, i) => {
                      const color = d.datasets[0].backgroundColor[i];
                      return {
                        text: ` ${label}`,
                        fillStyle: 'rgba(255,255,255,0)', // Hollow circle
                        strokeStyle: color,
                        lineWidth: 3,
                        pointStyle: 'circle',
                        hidden: false,
                        index: i
                      };
                    });
                  }
                }
              },
              tooltip: {
                callbacks: {
                  label(ctx) {
                    const t = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                    const pct = t > 0 ? Math.round((ctx.parsed / t) * 100) : 0;
                    return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
                  }
                }
              }
            }
          }
        });
      });
    },

    buildBarChart() {
      if (this.barChart) {
        this.barChart.destroy()
        this.barChart = null
      }

      const ctx = this.$refs.jobBarChart
      if (!ctx) return

      const labels = Object.keys(this.engineerData)
      const values = Object.values(this.engineerData)
      const self = this

      // Custom Plugin to draw horizontal average line
      const averageLinePlugin = {
        id: 'averageLinePlugin',
        afterDraw(chart) {
          const { ctx, chartArea: { left, right }, scales: { y } } = chart;
          const avgValue = parseFloat(self.avgCaseLoad);
          if (isNaN(avgValue) || avgValue <= 0) return;
          
          const yCoord = y.getPixelForValue(avgValue);
          
          ctx.save();
          ctx.beginPath();
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = '#333333';
          ctx.setLineDash([0, 0]); // Solid line
          ctx.moveTo(left, yCoord);
          ctx.lineTo(right, yCoord);
          ctx.stroke();
          
          // Optional: Add a label for the average
          ctx.fillStyle = '#333333';
          ctx.font = 'bold 11px sans-serif';
          ctx.textAlign = 'left';

          const labelPrefix = self.$t('__dashboardAvg') || 'Avg';
          const label = `${labelPrefix}: ${avgValue}`;

          // If the line is too close to the top, draw the text BELOW the line
          const textMargin = 5;
          const textY = (yCoord < 25) ? (yCoord + 15) : (yCoord - textMargin);
          
          ctx.fillText(label, left + 5, textY);
          
          ctx.restore();
        }
      };

      this.barChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels.length ? labels : ['—'],
          datasets: [{
            label: 'Cases',
            data: values.length ? values : [0],
            backgroundColor: '#3DC4C8',
            borderColor: '#3DC4C8',
            borderWidth: 1,
            borderRadius: 4,
          }]
        },
        plugins: [averageLinePlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 20, bottom: 10, left: 4, right: 4 } },
          onHover: (event, chartElement) => {
            event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
          },
          onClick: (event, elements) => {
            if (elements.length > 0) {
              const index = elements[0].index;
              const staffName = labels[index];
              if (staffName) {
                this.redirectPath(`/jobAssignmentList?staff=${encodeURIComponent(staffName)}`);
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { stepSize: 1, font: { size: 11 } },
              grid: { display: true, color: '#e0e0e0' }
            },
            x: {
              ticks: { font: { size: 11 } },
              grid: { display: false }
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: ctx => `Cases: ${ctx.parsed.y}`
              }
            }
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
}
.dashboard-outer-container {
  overflow-y: visible;
  background-color: #f5f5f5;
  padding: 16px !important;
}
@media (min-width: 600px) {
  .dashboard-outer-container {
    padding: 20px 30px !important;
  }
}
@media (min-width: 960px) {
  .dashboard-outer-container {
    padding: 40px 60px !important;
  }
}
@media (min-width: 1600px) {
  .dashboard-outer-container {
    padding: 40px 150px !important;
  }
}
.dashboard-inner-wrap {
  max-width: 1400px;
  margin: 0 auto;
}
.dashboard-card {
  border-radius: 12px !important;
  overflow: hidden;
}
.dashboard-card .v-card__text {
  padding: 16px !important;
}
@media (min-width: 960px) {
  .dashboard-card .v-card__text {
    padding: 24px !important;
  }
}
.dashboard-header {
  background-color: #ED6D00 !important;
  color: white !important;
  font-weight: 600;
  justify-content: center;
  font-size: 20px !important;
}
.stat-item {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  color: #666;
}
.stat-value {
  color: #ED6D00;
  font-size: 26px;
  font-weight: bold;
  margin-left: 8px;
}
.clickable-stat {
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 4px;
  padding: 4px 8px;
  margin-left: -8px;
}
.clickable-stat:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
.view-more-link {
  text-decoration: underline !important;
  text-transform: none !important;
  font-size: 14px;
  font-weight: 600;
  padding: 0 !important;
  height: auto !important;
  min-width: 0 !important;
}
.v-card-title {
  font-size: 18px;
  font-weight: 600;
}
</style>
